import { useEffect, useRef } from "react";
import { persistUTM, persistFbclid } from "utils/UTM";
import { persistShareReferral } from "utils/ShareReferral";
import useCookieConsent from "hooks/useCookieConsent";
import getUrlParameter from "helpers/getUrlParameter";

// Writes tracking cookies client-side because a cached CDN response never reaches Laravel, so the
// server-side middleware that would otherwise set them never runs. Used on any page that's both
// a possible landing target for tracked share/campaign links and subject to CDN caching (the
// campaign page and team page today).
const usePersistTrackingCookies = () => {
  const { consent, hasConsented } = useCookieConsent();
  // fbclid resolution can be delayed behind consent, by which point client-side routing may have
  // already dropped it from the URL, so it's captured on mount alongside the other params instead
  // of re-read from the URL when consent resolves.
  const initialFbclid = useRef(getUrlParameter("fbclid"));

  // pds/pdm and utm_* are necessary for attribution, not marketing analytics, so this runs
  // unconditionally rather than waiting on cookie consent. Runs once on mount, before any query
  // params drop off the URL via client-side routing, so the cookies still get set even when the
  // CDN serves a cached page and Laravel never runs.
  useEffect(() => {
    persistShareReferral();
    persistUTM();
  }, []);

  // fbclid (Facebook's own click ID) is analytics-gated, unlike utm_*/pds/pdm above. Runs once
  // consent resolves (and again if the visitor later changes their choice), using the value
  // captured on mount so a client-side route change in the meantime can't discard it.
  useEffect(() => {
    if (!consent || !hasConsented("analytics")) {
      return;
    }
    persistFbclid(initialFbclid.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasConsented is derived from `consent`, already a dep
  }, [consent]);
};

export default usePersistTrackingCookies;
